import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomDatePicker from "common/CustomDatePicker";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeClaimStatus, fetchClaims, fetchReimbursementHeads, fetchReimbursementMappedheads, fetchViewClaimList, saveClaim, sendClaimApproval, fetchTripmaster } from "store/actions";
import Swal from "sweetalert2";
import { convertToInputDate } from "common/dateFormat/formatDate";
import Loader from "common/pageLoader";

const TransactionClaim = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    masterheads,
    mappedheads,
    claims,
    viewClaimList,
    tripmasterList,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    masterheads: state.ReimbursementMasterReducer?.masterheads ?? [],
    mappedheads: state.ReimbursementMasterReducer?.mappedheads ?? [],
    claims: state.ReimbursementClaimReducer?.claims ?? [],
    viewClaimList: state.ReimbursementClaimReducer?.viewClaimList ?? [],
    tripmasterList: state.TripmasterReducer?.tripmasterList ?? [],

    loading: state.ReimbursementMasterReducer?.loading || state.DepartmentMaster?.loading || state.DesignationMaster?.loading,
    error: state.ReimbursementMasterReducer?.error || state.DepartmentMaster?.error || state.DesignationMaster?.error,
    success: state.ReimbursementClaimReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    const userId = authUserDetails[0].user_id
    dispatch(fetchReimbursementHeads(unitId, userId));
    dispatch(fetchClaims(unitId, userId));
    
    const tripFormData = new FormData();
    tripFormData.append('unit_id', unitId);
    tripFormData.append('create_by', userId);
    dispatch(fetchTripmaster(tripFormData));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchClaims(authDetails?.unit_id, authDetails?.user_id));
      formik.resetForm();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsEdit(false);
      setEditId(null);
      setAllocatedAmount(0);
      setBalanceAmount(0);
      setTripExpenses([]);
      setClaimRows([{ id: Date.now(), head: null, subHead: null, allocatedAmount: 0, balanceAmount: 0, amount: "", remarks: "", file: null, isEditing: true }]);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [allocatedAmount, setAllocatedAmount] = useState(0);
  const [filteredSubHeads, setFilteredSubHeads] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const fileInputRef = useRef(null);
  const [tripExpenses, setTripExpenses] = useState([]);
  const [claimRows, setClaimRows] = useState([{ id: Date.now(), head: null, subHead: null, allocatedAmount: 0, balanceAmount: 0, amount: "", remarks: "", file: null, isEditing: true }]);

  // Dynamic trip options from API
  const tripOptions = (tripmasterList || []).filter(trip => trip.b_active === 1).map(trip => ({
    value: trip.trip_id,
    label: trip.trip_name,
    from_date: trip.from_date,
    to_date: trip.to_date
  }));

  // Handle trip selection
  const handleTripChange = (selectedOption) => {
    formik.setFieldValue("trip", selectedOption);
    if (selectedOption) {
      formik.setFieldValue("tripFromDate", selectedOption.from_date);
      formik.setFieldValue("tripToDate", selectedOption.to_date);
    } else {
      formik.setFieldValue("tripFromDate", "");
      formik.setFieldValue("tripToDate", "");
    }
  };

  // Add new expense row
  const handleAddExpense = () => {
    setTripExpenses([...tripExpenses, {
      id: Date.now(),
      trip_expense: "",
      amount: "",
      attachment: null,
      details: ""
    }]);
  };

  // Add new claim row
  const handleAddClaimRow = () => {
    const newRow = {
      id: Date.now(),
      head: null,
      subHead: null,
      allocatedAmount: 0,
      balanceAmount: 0,
      amount: "",
      remarks: "",
      file: null,
      isEditing: true
    };
    setClaimRows([...claimRows, newRow]);
  };

  // Update claim row
  const handleClaimRowChange = (id, field, value) => {
    setClaimRows(prev => prev.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  // Remove claim row
  const handleRemoveClaimRow = (id) => {
    setClaimRows(prev => prev.filter(row => row.id !== id));
  };



  // Update expense row
  const handleExpenseChange = (id, field, value) => {
    setTripExpenses(prev => prev.map(exp =>
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  // Remove expense row
  const handleRemoveExpense = (id) => {
    setTripExpenses(prev => prev.filter(exp => exp.id !== id));
  };

  const activeClaims = Array.isArray(claims) ? claims : [];

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    toggleModal();
  };

  const formik = useFormik({
    initialValues: {
      trip: "",
      tripFromDate: "",
      tripToDate: "",
      travelDate: "",
      from: "",
      to: "",
      comments: "",
      file: null,
    },
    validationSchema: Yup.object({
      trip: Yup.object().required("Please select a Trip"),
      travelDate: Yup.date()
        .required("Please select a Date")
        .max(new Date(), "Future dates are not allowed")
        .typeError("Invalid date"),
      file: Yup.mixed()
        .nullable()
        .test(
          "fileSize",
          "File size must be less than or equal to 2MB",
          value => !value || value.size <= 2 * 1024 * 1024
        )
        .test(
          "fileType",
          "Unsupported file type",
          value =>
            !value ||
            ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (claimRows.length === 0) {
        Swal.fire('Warning', 'Please add at least one claim row', 'warning');
        return;
      }

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
      };

      const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
 
          reader.onload = () => resolve(reader.result); // base64 string
          reader.onerror = error => reject(error);
        });
      };

      const travelDate = values.travelDate instanceof Date
        ? values.travelDate.toISOString().split('T')[0]
        : values.travelDate;
        const reimbu = await Promise.all(
        claimRows.map(async (row) => {
          let fileBase64 = "";
          if (row.file) {
            fileBase64 = await convertFileToBase64(row.file);
          }
          return {
            head_id: row.head?.value || 0,
            subhead_id: row.subHead?.value || 0,
            amount: Number(row.amount) || 0,
            remarks: row.remarks || "",
            file: fileBase64,
          };
        })
      );

      const formData = new FormData();
      formData.append("claim_id", isEdit ? editId : 0);
      formData.append("trip_id", values.trip?.value || 0);
      formData.append("from_date", formatDate(values.tripFromDate) || "");
      formData.append("to_date", formatDate(values.tripToDate) || "");
      formData.append("date", travelDate);
      formData.append("from_place", values.from || "");
      formData.append("to_place", values.to || "");
      formData.append("comments", values.comments || "");
      formData.append("create_by", authDetails?.user_id || "");
      formData.append("lst_claim_savelist", JSON.stringify({ reimbu }));

      dispatch(saveClaim(formData));
    },
  });

  useEffect(() => {
    if (isEdit && formik.values.subHeads && mappedheads.length > 0) {
      const match = mappedheads.find(
        item => item.subhead_id === formik.values.subHeads.value
      );

      if (match) {
        const allocated = Number(match.to_amount || 0);
        const used = Number(match.from_amount || 0);

        setAllocatedAmount(allocated);
        setBalanceAmount(allocated - used);
      }
    }
  }, [mappedheads]);


  const handleEdit = (row) => {
    setIsEdit(true);
    setEditId(row.claim_id);
    formik.setValues({
      trip: { value: row.trip_id, label: row.trip_name },
      tripFromDate: row.from_date || "",
      tripToDate: row.to_date || "",
      travelDate: row.date ? (() => { const p = row.date.split('/'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : row.date; })() : "",
      from: row.from_place || "",
      to: row.to_place || "",
      comments: row.comments || "",
      file: null,
    });
    const rows = (row.details || []).map((d) => ({
      id: d.claim_id + "_" + d.subhead_id,
      head: { value: d.head_id, label: d.head_name },
      subHead: { value: d.subhead_id, label: d.subhead_name },
      allocatedAmount: 0,
      balanceAmount: 0,
      amount: d.amount,
      remarks: d.remarks || "",
      file: null,
      existingFilePath: d.file_path || null,
      isEditing: true,
    }));
    setClaimRows(rows);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (claim_id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("claim_id", claim_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeClaimStatus(formData));
      }
    });
  };


  const handleCheckboxChange = (claim_id) => {
    setSelectedClaims(prev =>
      prev.includes(claim_id)
        ? prev.filter(id => id !== claim_id)
        : [...prev, claim_id]
    );
  };

  const handleSendApproval = () => {
    if (selectedClaims.length === 0) {
      Swal.fire('Warning', 'Please select at least one claim to send', 'warning');
      return;
    }

    const reimb = selectedClaims.map(claim_id => ({ claim_id: claim_id.toString() }));
    const formData = new FormData();
    formData.append('lst_reim_send_approval', JSON.stringify({ reimb }));
    formData.append('create_by', authDetails?.user_id?.toString() || "");

    dispatch(sendClaimApproval(formData));
    setSelectedClaims([]);
  };

  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => activeClaims.length - row.index,
      className: "serial-no",
      applyHeaderClass: true,
    },
    {
      Header: "Action",
      Cell: (cellProps) => {
        const { claim_id } = cellProps.row.original;
        return (
          <div className="d-flex gap-3">
            {/* View Icon */}
            <Link to="#" className="text-info" onClick={() => handleView(cellProps.row.original)}>
              <i className="mdi mdi-eye font-size-18" id={`view-tooltip-${cellProps.row.id}`} />
              <UncontrolledTooltip placement="top" target={`view-tooltip-${cellProps.row.id}`}>View</UncontrolledTooltip>
            </Link>

            {/* Edit Icon */}
            <Link to="#" className="text-success" onClick={() => handleEdit(cellProps.row.original)}>
              <i className="bx bxs-edit font-size-18" id={`edit-tooltip-${cellProps.row.id}`} />
              <UncontrolledTooltip placement="top" target={`edit-tooltip-${cellProps.row.id}`}>Edit</UncontrolledTooltip>
            </Link>

            {/* Delete Icon */}
            <Link to="#" className="text-danger" onClick={() => handleDelete(claim_id)}>
              <i className="mdi mdi-delete font-size-18" id={`delete-tooltip-${cellProps.row.id}`} />
              <UncontrolledTooltip placement="top" target={`delete-tooltip-${cellProps.row.id}`}>Delete</UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
    { Header: "Trip Name", accessor: "trip_name" },
    { Header: "From Date", accessor: "from_date", Cell: ({ value }) => value ? value.split(" ")[0] : "-" },
    { Header: "To Date", accessor: "to_date", Cell: ({ value }) => value ? value.split(" ")[0] : "-" },
    { Header: "Date", accessor: "date", Cell: ({ value }) => value ? value.split(" ")[0] : "-" },
    { Header: "From Place", accessor: "from_place" },
    { Header: "To Place", accessor: "to_place" },
    {
      Header: "Total Amount",
      Cell: ({ row }) => {
        const total = (row.original.details || []).reduce((sum, d) => sum + Number(d.amount || 0), 0);
        return total.toFixed(2);
      }
    },
  ];

  const SelectcustomStyles = {
    control: (base) => ({ ...base, minHeight: 33.1, maxHeight: 33.1 }),
    dropdownIndicator: (base) => ({ ...base, padding: "2px 8px" }),
    clearIndicator: (base) => ({ ...base, padding: "2px 8px" }),
    valueContainer: (base) => ({ ...base, padding: "0px 8px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
  };

  // const balanceAmount =
  //   Number(allocatedAmount || 0) - Number(formik.values.amount || 0);s

  const handleCancel = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "All unsaved changes will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        formik.resetForm();
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setIsEdit(false);
        setEditId(null);
        setAllocatedAmount(0);
        setBalanceAmount(0);
        setTripExpenses([]);
        setClaimRows([{ id: Date.now(), head: null, subHead: null, allocatedAmount: 0, balanceAmount: 0, amount: "", remarks: "", file: null, isEditing: true }]);
      }
    });
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Reimbursement" breadcrumbItem="Claim" />
        <Card className="mb-4">
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label>Trip <span className="text-danger">*</span></Label>
                    <Select
                      options={tripOptions}
                      value={formik.values.trip || null}
                      onChange={handleTripChange}
                      className="hrms-select"
                      classNamePrefix="hrms-select"
                      styles={SelectcustomStyles}
                    />
                    {formik.touched.trip && formik.errors.trip && (
                      <div className="text-danger">{formik.errors.trip}</div>
                    )}
                  </FormGroup>
                </Col>
                {formik.values.trip && (
                  <>
                    <Col md="3">
                      <FormGroup>
                        <Label>From Date</Label>
                        <Input
                          type="text"
                          value={formik.values.tripFromDate}
                          readOnly
                          className="bg-light"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>To Date</Label>
                        <Input
                          type="text"
                          value={formik.values.tripToDate}
                          readOnly
                          className="bg-light"
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                <Col md="3">
                  <FormGroup>
                    <Label for="travelDate">Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      value={formik.values.travelDate}
                      maxDate={new Date()}
                      onChange={(date) => formik.setFieldValue("travelDate", date)}
                      onBlur={() => formik.setFieldTouched("travelDate", true)}
                    />
                    {formik.touched.travelDate && formik.errors.travelDate && (
                      <div className="text-danger">{formik.errors.travelDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>From Place</Label>
                    <Input
                      type="text"
                      name="from"
                      value={formik.values.from}
                      onChange={formik.handleChange}
                      className={
                        formik.touched.from && formik.errors.from
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formik.touched.from && formik.errors.from && (
                      <div className="text-danger">
                        {formik.errors.from}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>To Place</Label>
                    <Input
                      type="text"
                      name="to"
                      value={formik.values.to}
                      onChange={formik.handleChange}
                      className={
                        formik.touched.to && formik.errors.to
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formik.touched.to && formik.errors.to && (
                      <div className="text-danger">
                        {formik.errors.to}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Comments</Label>
                    <Input
                      type="text"
                      name="comments"
                      value={formik.values.comments}
                      onChange={formik.handleChange}
                      className={
                        formik.touched.comments && formik.errors.comments
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formik.touched.comments && formik.errors.comments && (
                      <div className="text-danger">
                        {formik.errors.comments}</div>
                    )}
                  </FormGroup>
                </Col>
                {/* <Col md="3">
                  <FormGroup>
                    <Label>Overall File Attach</Label>
                    <Input
                      innerRef={fileInputRef}
                      type="file"
                      name="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(event) =>
                        formik.setFieldValue("file", event.target.files[0])
                      }
                    />
                    {formik.touched.file && formik.errors.file && (
                      <div className="text-danger">
                        {formik.errors.file}</div>
                    )}
                  </FormGroup>
                </Col> */}
                <Col md="12" className="d-flex align-items-center justify-content-end mt-4 mob-center">
                  {selectedClaims.length > 0 && (
                    <Button
                      type="button"
                      color="primary"
                      className="btn me-2"
                      onClick={handleSendApproval}
                    >
                      <i className="fas fa-paper-plane me-1" /> Send
                    </Button>
                  )}
                  <Button type="submit" className="btn button-save me-2">
                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                  </Button>
                  <Button
                    color="secondary"
                    type="button"
                    onClick={handleCancel}
                    className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>

              <Row className="mt-4">
                  <Col md="12">
                    <div className="table-responsive">
                      <Table bordered className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "60px" }} className="text-center">S.No</th>
                            <th style={{ width: "80px" }} className="text-center">Action</th>
                            <th style={{ width: "12%" }}>Head</th>
                            <th style={{ width: "12%" }}>Sub Head</th>
                            <th style={{ width: "10%" }}>Allocated Amount</th>
                            <th style={{ width: "10%" }}>Balance Amount</th>
                            <th style={{ width: "10%" }}>Amount</th>
                            <th style={{ width: "15%" }}>Remarks</th>
                            <th style={{ width: "15%" }}>File Attach</th>
                          </tr>
                        </thead>
                        <tbody>
                          {claimRows.map((row, index) => (
                            <tr key={row.id}>
                              <td className="text-center align-middle">{index + 1}</td>
                              <td className="text-center align-middle">
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  {claimRows.length > 1 && (
                                    <Link to="#" className="text-danger" onClick={() => handleRemoveClaimRow(row.id)}>
                                      <i className="mdi mdi-delete font-size-18" />
                                    </Link>
                                  )}
                                  {index === claimRows.length - 1 && (
                                    <Link to="#" className="text-success" onClick={handleAddClaimRow}>
                                      <i className="bx bx-plus-circle font-size-18" />
                                    </Link>
                                  )}
                                </div>
                              </td>
                              <td className="align-middle">
                                <Select
                                  options={(masterheads || []).map(emp => ({ value: emp.head_id, label: emp.head_name }))}
                                  value={row.head}
                                  onChange={(val) => {
                                    handleClaimRowChange(row.id, "head", val);
                                    handleClaimRowChange(row.id, "subHead", null);
                                    handleClaimRowChange(row.id, "allocatedAmount", 0);
                                    handleClaimRowChange(row.id, "balanceAmount", 0);
                                    if (val) {
                                      dispatch(fetchReimbursementMappedheads(authDetails?.unit_id, val.value, authDetails?.user_id));
                                    }
                                  }}
                                  className="hrms-select"
                                  classNamePrefix="hrms-select"
                                  styles={SelectcustomStyles}
                                  menuPosition="fixed"
                                  menuPortalTarget={document.body}
                                />
                              </td>
                              <td className="align-middle">
                                <Select
                                  options={(mappedheads || []).map(emp => ({ value: emp.subhead_id, label: emp.subheads_name, to_amount: emp.to_amount, from_amount: emp.from_amount }))}
                                  value={row.subHead}
                                  onChange={(val) => {
                                    handleClaimRowChange(row.id, "subHead", val);
                                    if (val) {
                                      const allocated = Number(val.to_amount || 0);
                                      const used = Number(val.from_amount || 0);
                                      const balance = allocated - used;
                                      handleClaimRowChange(row.id, "allocatedAmount", allocated);
                                      handleClaimRowChange(row.id, "balanceAmount", balance);
                                    }
                                  }}
                                  isDisabled={!row.head}
                                  className="hrms-select"
                                  classNamePrefix="hrms-select"
                                  styles={SelectcustomStyles}
                                  menuPosition="fixed"
                                  menuPortalTarget={document.body}
                                />
                              </td>
                              <td className="align-middle">
                                <Input
                                  type="number"
                                  value={row.allocatedAmount || 0}
                                  readOnly
                                  className="bg-light"
                                  bsSize="sm"
                                />
                              </td>
                              <td className="align-middle">
                                <Input
                                  type="number"
                                  value={row.balanceAmount >= 0 ? row.balanceAmount : 0}
                                  readOnly
                                  className="bg-light fw-bold"
                                  bsSize="sm"
                                />
                              </td>
                              <td className="align-middle">
                                <Input
                                  type="number"
                                  value={row.amount}
                                  onChange={(e) => handleClaimRowChange(row.id, "amount", e.target.value)}
                                  placeholder="Amount"
                                  min="0"
                                  bsSize="sm"
                                />
                              </td>
                              <td className="align-middle">
                                <Input
                                  type="text"
                                  value={row.remarks}
                                  onChange={(e) => handleClaimRowChange(row.id, "remarks", e.target.value)}
                                  placeholder="Remarks"
                                  bsSize="sm"
                                />
                              </td>
                              <td className="align-middle">
                                <div className="d-flex align-items-center form-control p-0" style={{ overflow: "hidden" }}>
                                  <label htmlFor={`file-${row.id}`} className="mb-0 px-2 py-1" style={{ background: "#e9ecef", borderRight: "1px solid #ced4da", cursor: "pointer", whiteSpace: "nowrap", fontSize: "0.875rem" }}>Choose file</label>
                                  <span className="px-2 text-truncate" style={{ fontSize: "0.875rem", color: row.file || row.existingFilePath ? "#495057" : "#6c757d" }}>
                                    {row.file ? row.file.name : row.existingFilePath ? row.existingFilePath.split('/').pop() : "No file chosen"}
                                  </span>
                                </div>
                                <input
                                  id={`file-${row.id}`}
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  style={{ display: "none" }}
                                  onChange={(e) => handleClaimRowChange(row.id, "file", e.target.files[0] || null)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer columns={columns} data={activeClaims && activeClaims.length > 0 ? activeClaims : []} isGlobalFilter={true} customPageSize={10} />
          </CardBody>
        </Card>

        {isModalOpen && selectedRow && (
          <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered>
            <ModalHeader toggle={toggleModal}>Claim Details — {selectedRow.trip_name}</ModalHeader>
            <ModalBody>
              <Row className="mb-3">
                <Col md="4"><strong>From Place:</strong> {selectedRow.from_place}</Col>
                <Col md="4"><strong>To Place:</strong> {selectedRow.to_place}</Col>
                <Col md="4"><strong>Date:</strong> {selectedRow.date?.split(" ")[0]}</Col>
              </Row>
              <Table className="table table-bordered">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Head</th>
                    <th>Sub Head</th>
                    <th>Amount</th>
                    <th>Remarks</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedRow.details || []).map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.head_name}</td>
                      <td>{item.subhead_name}</td>
                      <td>{Number(item.amount).toFixed(2)}</td>
                      <td>{item.remarks || "-"}</td>
                      <td>
                        {item.file_path ? (
                          <a href={`${process.env.REACT_APP_IMAGE_BASE_URL}${item.file_path}`} target="_blank" rel="noopener noreferrer">
                            <i className="mdi mdi-file-check text-info font-size-18" />
                          </a>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ModalBody>
          </Modal>
        )}
      </Container>
    </div>
  );
};

export default TransactionClaim;
